import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { ExamPrescriptionItemInput } from "@/lib/validations/exam-prescription";
import { parseExamLines } from "@/lib/prescriptions/editorDraft";
import { cn } from "@/lib/utils";
import { ExpandedItemShell } from "./ExpandedItemShell";

interface ExamTypeLike {
  id: string;
  name: string;
}

export function ExamItemsEditor({
  initial,
  ordinal,
  mobile = false,
  examTypes,
  loadingExamTypes,
  onSearchChange,
  onCreateExamType,
  creatingExamType,
  onCommit,
  onCancel,
  onDraftChange,
}: {
  initial: ExamPrescriptionItemInput | null;
  ordinal: number;
  mobile?: boolean;
  examTypes: ExamTypeLike[];
  loadingExamTypes: boolean;
  onSearchChange: (value: string) => void;
  onCreateExamType: (name: string) => Promise<ExamTypeLike>;
  creatingExamType: boolean;
  onCommit: (rows: ExamPrescriptionItemInput[]) => void;
  onCancel: () => void;
  onDraftChange: (rows: ExamPrescriptionItemInput[]) => void;
}) {
  const { t } = useTranslation("prescriptions");
  const isEdit = Boolean(initial);
  const [mode, setMode] = useState<"fields" | "free">("fields");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ExamPrescriptionItemInput[]>(initial ? [initial] : []);
  const [freeText, setFreeText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo<ExamPrescriptionItemInput[]>(
    () =>
      mode === "free"
        ? parseExamLines(freeText).map((line) => ({ exam_type_id: "", exam_name: line.exam_name, instructions: line.instructions }))
        : selected,
    [mode, freeText, selected],
  );

  useEffect(() => {
    onDraftChange(rows.filter((row) => row.exam_name.trim()));
  }, [rows, onDraftChange]);
  useEffect(() => () => onDraftChange([]), [onDraftChange]);

  const toggle = (exam: ExamTypeLike) =>
    setSelected((current) =>
      current.some((row) => row.exam_type_id === exam.id)
        ? current.filter((row) => row.exam_type_id !== exam.id)
        : [...current, { exam_type_id: exam.id, exam_name: exam.name, instructions: "" }],
    );
  const setInstructions = (index: number, value: string) =>
    setSelected((current) => current.map((row, i) => (i === index ? { ...row, instructions: value } : row)));

  const trimmed = search.trim();
  const hasExact = examTypes.some((exam) => exam.name.toLowerCase().trim() === trimmed.toLowerCase());
  const label = "text-sm font-medium";
  const inputCls = mobile ? "h-12 text-base" : undefined;
  const dirty = isEdit ? JSON.stringify(selected) !== JSON.stringify([initial]) : selected.length > 0 || freeText.trim().length > 0;
  const count = rows.filter((row) => row.exam_name.trim()).length;

  const confirm = () => {
    const valid = rows.filter((row) => row.exam_name.trim());
    if (valid.length === 0) {
      setError(t("editor.exam.required"));
      return;
    }
    onCommit(valid);
  };

  return (
    <ExpandedItemShell
      title={isEdit ? t("editor.exam.editTitle") : t("editor.exam.addTitle")}
      ordinal={ordinal}
      mobile={mobile}
      dirty={dirty}
      onCancel={onCancel}
      onConfirm={confirm}
      confirmLabel={isEdit ? t("editor.exam.saveItem") : t("editor.exam.addCount", { count })}
      mode={
        isEdit
          ? undefined
          : {
              value: mode,
              onChange: setMode,
              labels: { fields: t("editor.exam.catalog"), fieldsHint: t("editor.exam.catalogHint"), free: t("editor.mode.free"), freeHint: t("editor.exam.freeHint") },
            }
      }
    >
      {isEdit ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="exam-edit-name" className={label}>{t("examComposer.exam")}</Label>
            <Input id="exam-edit-name" value={selected[0]?.exam_name ?? ""} onChange={(e) => setSelected([{ ...selected[0], exam_name: e.target.value }])} className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="exam-edit-instr" className={label}>{t("examComposer.instructionsOptional")}</Label>
            <Textarea id="exam-edit-instr" rows={2} value={selected[0]?.instructions ?? ""} onChange={(e) => setSelected([{ ...selected[0], instructions: e.target.value }])} className={cn("rounded-[10px]", mobile && "text-base")} />
          </div>
        </div>
      ) : mode === "fields" ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="exam-search" className={label}>{t("editor.exam.search")}</Label>
            <Input id="exam-search" value={search} onChange={(e) => { setSearch(e.target.value); onSearchChange(e.target.value); }} placeholder={t("examComposer.examPlaceholder")} className={inputCls} />
          </div>
          <ul className="max-h-56 divide-y overflow-auto rounded-xl border" aria-label={t("editor.exam.catalog")}>
            {examTypes.length === 0 ? (
              <li className="px-3 py-3 text-sm text-muted-foreground">{loadingExamTypes ? t("examComposer.loadingExams") : t("examComposer.emptyExams")}</li>
            ) : (
              examTypes.map((exam) => {
                const checked = selected.some((row) => row.exam_type_id === exam.id);
                return (
                  <li key={exam.id}>
                    <label className="flex min-h-11 cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-secondary/60">
                      <Checkbox checked={checked} onCheckedChange={() => toggle(exam)} />
                      <span className="min-w-0 flex-1">{exam.name}</span>
                    </label>
                  </li>
                );
              })
            )}
          </ul>
          {trimmed && !hasExact ? (
            <Button
              type="button"
              variant="ghost"
              className="w-full justify-start text-primary"
              disabled={creatingExamType}
              onClick={async () => {
                const created = await onCreateExamType(trimmed);
                setSelected((current) => [...current, { exam_type_id: created.id, exam_name: created.name, instructions: "" }]);
                setSearch("");
                onSearchChange("");
              }}
            >
              <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {t("examComposer.createExam", { value: trimmed })}
            </Button>
          ) : null}
          {selected.length > 0 ? (
            <div className="space-y-2">
              <p className={label}>{t("editor.exam.instructionsPerExam")}</p>
              {selected.map((row, index) => (
                <div key={row.exam_type_id || index} className={cn("grid items-center gap-2", !mobile && "grid-cols-[1fr_2fr]")}>
                  <span className="text-sm font-medium">{row.exam_name}</span>
                  <Input aria-label={`${t("examComposer.instructionsOptional")} — ${row.exam_name}`} value={row.instructions ?? ""} onChange={(e) => setInstructions(index, e.target.value)} placeholder={t("examComposer.instructionsPlaceholder")} className={inputCls} />
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="exam-free" className={label}>{t("editor.exam.onePerLine")}</Label>
          <Textarea id="exam-free" rows={mobile ? 6 : 7} value={freeText} onChange={(e) => setFreeText(e.target.value)} className={cn("rounded-xl border-2 border-primary", mobile && "text-base")} placeholder={t("editor.exam.freePlaceholder")} />
          <p className="text-xs text-muted-foreground">{t("editor.exam.freeHelp")}</p>
        </div>
      )}
      {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
    </ExpandedItemShell>
  );
}
